import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Mail,
  FileText,
  Package,
  CheckCircle,
  UserPlus,
  Eye,
  Send,
  Settings,
  Loader2,
  X,
  ExternalLink,
  Gift,
  Paperclip,
  Globe,
  BarChart3,
  TrendingUp,
  MousePointerClick,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  XCircle,
  CreditCard
} from 'lucide-react';
import { emailManagementApi, EmailTypeInfo, EmailPreviewResponse, EmailStatsSummaryResponse } from '../lib/api';
import { useToast } from '@/components/ui/Toast';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Package,
  CheckCircle,
  UserPlus,
  RotateCcw,
  XCircle,
  CreditCard,
};

// Color mapping for Tailwind classes
const colorClasses: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-600',
    badge: 'bg-green-100 text-green-700',
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-600',
    badge: 'bg-purple-100 text-purple-700',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-600',
    badge: 'bg-red-100 text-red-700',
  },
};

export default function EmailManagementPage() {
  const [selectedEmail, setSelectedEmail] = useState<EmailTypeInfo | null>(null);
  const [previewLang, setPreviewLang] = useState('es');
  const [previewOrderId, setPreviewOrderId] = useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [statsPeriod, setStatsPeriod] = useState(30);
  const toast = useToast();

  // Fetch email types
  const { data: typesData, isLoading } = useQuery({
    queryKey: ['email-types'],
    queryFn: emailManagementApi.getTypes,
  });

  // Fetch stats summary
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['email-stats-summary', statsPeriod],
    queryFn: () => emailManagementApi.getStatsSummary(statsPeriod),
  });

  // Preview query
  const {
    data: previewData,
    isLoading: previewLoading,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ['email-preview', selectedEmail?.id, previewLang, previewOrderId],
    queryFn: () =>
      emailManagementApi.getPreview(
        selectedEmail!.id,
        previewLang,
        previewOrderId ? parseInt(previewOrderId) : undefined
      ),
    enabled: showPreviewModal && !!selectedEmail,
  });

  // Send test mutation
  const sendTestMutation = useMutation({
    mutationFn: () =>
      emailManagementApi.sendTestEmail(
        selectedEmail!.id,
        testEmail,
        previewLang,
        previewOrderId ? parseInt(previewOrderId) : undefined
      ),
    onSuccess: (data) => {
      toast.success(data.message);
      setShowTestModal(false);
      setTestEmail('');
    },
    onError: (error: Error) => {
      toast.error(`Error enviando email: ${error.message}`);
    },
  });

  const handlePreview = (emailType: EmailTypeInfo) => {
    setSelectedEmail(emailType);
    setShowPreviewModal(true);
  };

  const handleTest = (emailType: EmailTypeInfo) => {
    setSelectedEmail(emailType);
    setShowTestModal(true);
  };

  const emailTypes = typesData?.types || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-7 w-7 text-blue-600" />
            Gestion de Emails
          </h1>
          <p className="text-gray-500 mt-1">
            Configura y previsualiza los emails transaccionales del sistema
          </p>
        </div>
        <a
          href="/email-promos"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Gift className="h-4 w-4" />
          Productos Promocionales
        </a>
      </div>

      {/* Email Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {emailTypes.map((emailType) => {
          const IconComponent = iconMap[emailType.icon] || Mail;
          const colors = colorClasses[emailType.color] || colorClasses.blue;

          return (
            <div
              key={emailType.id}
              className={`relative border rounded-xl overflow-hidden ${colors.border} ${
                emailType.is_active ? colors.bg : 'bg-gray-50 border-gray-200'
              }`}
            >
              {/* Status badge */}
              <div className="absolute top-3 right-3">
                {emailType.is_active ? (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
                    Activo
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                    Inactivo
                  </span>
                )}
              </div>

              <div className="p-5">
                {/* Icon and Title */}
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={`p-2 rounded-lg ${
                      emailType.is_active ? colors.badge : 'bg-gray-200'
                    }`}
                  >
                    <IconComponent
                      className={`h-6 w-6 ${
                        emailType.is_active ? colors.text : 'text-gray-500'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-semibold ${
                        emailType.is_active ? 'text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {emailType.name}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {emailType.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {emailType.has_promos && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-gray-600 border">
                      <Gift className="h-3 w-3" />
                      Promos
                    </span>
                  )}
                  {emailType.has_attachment && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-gray-600 border">
                      <Paperclip className="h-3 w-3" />
                      Adjunto
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-gray-600 border">
                    <Globe className="h-3 w-3" />
                    {emailType.languages.length} idiomas
                  </span>
                </div>

                {/* Actions */}
                {emailType.is_active && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePreview(emailType)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="h-4 w-4" />
                      Vista Previa
                    </button>
                    <button
                      onClick={() => handleTest(emailType)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 border border-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700"
                    >
                      <Send className="h-4 w-4" />
                      Probar
                    </button>
                  </div>
                )}

                {!emailType.is_active && (
                  <div className="text-center py-2 text-sm text-gray-400">
                    Proximamente
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Statistics Section */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Stats Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-gray-600" />
            <h2 className="font-semibold text-gray-900">Estadisticas de Emails</h2>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statsPeriod}
              onChange={(e) => setStatsPeriod(parseInt(e.target.value))}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white"
            >
              <option value={7}>Ultimos 7 dias</option>
              <option value={30}>Ultimos 30 dias</option>
              <option value={90}>Ultimos 90 dias</option>
            </select>
            <button
              onClick={() => refetchStats()}
              disabled={statsLoading}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-4 w-4 text-gray-600 ${statsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stats Content */}
        {statsLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : statsData?.success ? (
          <div className="p-4">
            {/* Totals Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Enviados</p>
                <p className="text-2xl font-bold text-blue-700">{statsData.totals.sent.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Entregados</p>
                <p className="text-2xl font-bold text-green-700">{statsData.totals.delivered.toLocaleString()}</p>
                <p className="text-xs text-green-600">{statsData.totals.delivery_rate}%</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Abiertos</p>
                <p className="text-2xl font-bold text-purple-700">{statsData.totals.unique_opens.toLocaleString()}</p>
                <p className="text-xs text-purple-600">{statsData.totals.open_rate}%</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-xs text-amber-600 uppercase tracking-wide mb-1">Clicks</p>
                <p className="text-2xl font-bold text-amber-700">{statsData.totals.unique_clicks.toLocaleString()}</p>
                <p className="text-xs text-amber-600">{statsData.totals.click_rate}%</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs text-red-600 uppercase tracking-wide mb-1">Rebotes</p>
                <p className="text-2xl font-bold text-red-700">
                  {(statsData.totals.hard_bounces + statsData.totals.soft_bounces).toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Spam</p>
                <p className="text-2xl font-bold text-gray-700">{statsData.totals.spam_reports.toLocaleString()}</p>
              </div>
            </div>

            {/* Stats by Type */}
            <h3 className="text-sm font-medium text-gray-700 mb-3">Desglose por tipo de email</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Tipo</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Enviados</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Entregados</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Tasa Entrega</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Abiertos</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Tasa Apertura</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Clicks</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Tasa Click</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Rebotes</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(statsData.by_type).map(([type, stats]) => {
                    const emailType = emailTypes.find(t => t.id === type);
                    const colors = colorClasses[emailType?.color || 'blue'];
                    return (
                      <tr key={type} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                            {emailType?.name || type}
                          </span>
                        </td>
                        <td className="text-right py-2 px-3 font-medium">{stats.sent.toLocaleString()}</td>
                        <td className="text-right py-2 px-3">{stats.delivered.toLocaleString()}</td>
                        <td className="text-right py-2 px-3">
                          <span className={stats.delivery_rate >= 95 ? 'text-green-600' : stats.delivery_rate >= 90 ? 'text-yellow-600' : 'text-red-600'}>
                            {stats.delivery_rate}%
                          </span>
                        </td>
                        <td className="text-right py-2 px-3">{stats.unique_opens.toLocaleString()}</td>
                        <td className="text-right py-2 px-3">
                          <span className={stats.open_rate >= 30 ? 'text-green-600' : stats.open_rate >= 20 ? 'text-yellow-600' : 'text-gray-600'}>
                            {stats.open_rate}%
                          </span>
                        </td>
                        <td className="text-right py-2 px-3">{stats.unique_clicks.toLocaleString()}</td>
                        <td className="text-right py-2 px-3">
                          <span className={stats.click_rate >= 5 ? 'text-green-600' : stats.click_rate >= 2 ? 'text-yellow-600' : 'text-gray-600'}>
                            {stats.click_rate}%
                          </span>
                        </td>
                        <td className="text-right py-2 px-3 text-gray-500">
                          {(stats.hard_bounces + stats.soft_bounces).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Stats Period Info */}
            <div className="mt-4 text-xs text-gray-500 flex items-center gap-1">
              <span>Periodo: {statsData.start_date} a {statsData.end_date}</span>
              <span className="text-gray-300">|</span>
              <span>Datos de Brevo (Sendinblue)</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <AlertTriangle className="h-5 w-5 mr-2" />
            No se pudieron cargar las estadisticas
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configuracion de Emails
        </h3>
        <p className="text-sm text-blue-700 mb-3">
          Los productos promocionales se configuran en la seccion{' '}
          <a href="/email-promos" className="underline font-medium">
            Productos Promocionales
          </a>
          . Puedes elegir que productos mostrar en cada tipo de email.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-lg p-3 border border-blue-100">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Factura</p>
            <p className="text-sm text-gray-700">
              Se envia automaticamente al sincronizar facturas desde Factusol
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-100">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Enviado</p>
            <p className="text-sm text-gray-700">
              Se envia automaticamente al crear etiqueta de envio
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-100">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Entregado</p>
            <p className="text-sm text-gray-700">
              Se envia automaticamente al detectar entrega via tracking
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Vista Previa: {selectedEmail.name}
                </h2>
                <p className="text-sm text-gray-500">
                  {previewData?.subject || 'Cargando...'}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Idioma:</label>
                <select
                  value={previewLang}
                  onChange={(e) => setPreviewLang(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm"
                >
                  <option value="es">Espanol</option>
                  <option value="en">English</option>
                  <option value="pt">Portugues</option>
                  <option value="fr">Francais</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Pedido ID:</label>
                <input
                  type="text"
                  value={previewOrderId}
                  onChange={(e) => setPreviewOrderId(e.target.value)}
                  placeholder="Opcional"
                  className="px-3 py-1.5 border rounded-lg text-sm w-24"
                />
              </div>
              <button
                onClick={() => refetchPreview()}
                disabled={previewLoading}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {previewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Actualizar'
                )}
              </button>
              {previewData?.promo_products_count !== undefined && (
                <span className="text-sm text-gray-500">
                  {previewData.promo_products_count} productos promocionales
                </span>
              )}
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              {previewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : previewData?.html ? (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <iframe
                    srcDoc={previewData.html}
                    className="w-full h-[600px] border-0"
                    title="Email Preview"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  {previewData?.error || 'No se pudo cargar la vista previa'}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-500">
                {previewData?.note}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setShowTestModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 inline mr-1" />
                  Enviar Prueba
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Enviar Email de Prueba
                </h2>
                <p className="text-sm text-gray-500">{selectedEmail.name}</p>
              </div>
              <button
                onClick={() => setShowTestModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de destino
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Idioma
                  </label>
                  <select
                    value={previewLang}
                    onChange={(e) => setPreviewLang(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="es">Espanol</option>
                    <option value="en">English</option>
                    <option value="pt">Portugues</option>
                    <option value="fr">Francais</option>
                    <option value="de">Deutsch</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pedido ID (opcional)
                  </label>
                  <input
                    type="text"
                    value={previewOrderId}
                    onChange={(e) => setPreviewOrderId(e.target.value)}
                    placeholder="12345"
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">
                  El email se enviara con el asunto marcado como [TEST]
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={() => sendTestMutation.mutate()}
                disabled={!testEmail || sendTestMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {sendTestMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 inline mr-1 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 inline mr-1" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
